import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { AdminPanelSettings, CheckCircle } from '@mui/icons-material';
import { AdminService } from '../services/adminService';

const steps = ['Configuration initiale', 'Création du compte', 'Finalisation'];

const FirstAdminSetupScreen: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate first step
      if (!formData.firstName || !formData.lastName || !formData.email) {
        setError('Veuillez remplir tous les champs obligatoires');
        return;
      }
      if (!formData.email.includes('@')) {
        setError('Veuillez entrer une adresse email valide');
        return;
      }
    } else if (activeStep === 1) {
      // Validate second step
      if (!formData.password || formData.password.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }
    }
    
    setError('');
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Create first admin
      await AdminService.createFirstAdmin({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
      });
      
      setSuccess(true);
      setLoading(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erreur lors de la création');
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informations personnelles
            </Typography>
            <TextField
              fullWidth
              label="Prénom"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Nom"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Adresse email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Téléphone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              margin="normal"
            />
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sécurité du compte
            </Typography>
            <TextField
              fullWidth
              label="Mot de passe"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              margin="normal"
              required
              helperText="Minimum 6 caractères"
            />
            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              margin="normal"
              required
            />
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Vérification finale
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Vérifiez que toutes les informations sont correctes avant de créer le compte administrateur principal.
            </Typography>
            
            <Box sx={{ textAlign: 'left', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2">
                <strong>Prénom:</strong> {formData.firstName}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Nom:</strong> {formData.lastName}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Email:</strong> {formData.email}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Téléphone:</strong> {formData.phone || 'Non renseigné'}
              </Typography>
            </Box>
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center', width: '100%' }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Compte administrateur créé avec succès !
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              Votre compte administrateur principal a été configuré. Vous pouvez maintenant vous connecter et commencer à gérer la plateforme.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.location.href = '/login'}
            >
              Se connecter
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <AdminPanelSettings color="primary" sx={{ fontSize: 64, mb: 2 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              Configuration initiale
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Créez le premier compte administrateur pour configurer la plateforme
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Retour
            </Button>
            
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Création...' : 'Créer le compte'}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext}>
                  Suivant
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default FirstAdminSetupScreen;
